import { Component, inject, OnInit } from '@angular/core';
import { CompanyService } from "../../../app/api/api/company.service";
import { ModelsCompanyPosition } from "../../../app/api/model/models";
import { ApiCompanyPositionDataResponse } from "../../../app/api/model/apiCompanyPositionDataResponse";
import { ApiCompanyPositionListDataResponse } from "../../../app/api/model/apiCompanyPositionListDataResponse";
import { ButtonModule } from "primeng/button";
import { TableModule } from "primeng/table";
import { RippleModule } from "primeng/ripple";
import { ToolbarModule } from "primeng/toolbar";
import { InputTextModule } from "primeng/inputtext";
import { ConfirmationService, MessageService } from "primeng/api";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";
import { HrPositionsForm } from './hr.positions.form';
import { Card } from 'primeng/card';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

interface UserPosition {
  id: number;
  name: string;
  requirements: string;
  scheduling_display_name: string;
  day_limit: number;
  week_limit: number;
}

@Component({
  selector: 'app-hr-positions',
  template: `
    <p-toast></p-toast>
    <p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>
    <app-hr-position-form
      [visible]="positionDialog" [position]="position" (onSave)="savePosition($event)"
      (onCancel)="hideDialog()"></app-hr-position-form>

    <div class="pt-4">
      @if (ENABLE_ADD) {
        <div class="flex justify-between mb-4">
          <div>
            <p-button
              severity="success"
              [label]="'Create' | transloco"
              icon="pi pi-plus"
              (click)="openNew()"/>
          </div>
          <div></div>
        </div>
      }
      <p-table
        #dt
        [value]="positions"
        [rowHover]="true"
        dataKey="id"
        tableStyleClass="mb-4">
        <ng-template pTemplate="header">
          <tr class="hidden md:table-row">
            <th pSortableColumn="name">{{ 'Name' | transloco }}
              <p-sortIcon field="name"></p-sortIcon>
            </th>
            <th class="w-1"></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-position>
          <!-- Desktop Row -->
          <tr class="hidden md:table-row">
            <td>
              <div>{{ position.name }}</div>
              @if (position.position?.role === 'manager') {
                <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                  {{ 'This position has access to the management portal and the ability to add employees to the company' | transloco }}
                </div>
              }
            </td>
            <td class="w-1">
              <button pButton pRipple icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2"
                      (click)="editPosition(position)"></button>
            </td>
          </tr>
          <!-- Mobile Card -->
          <tr class="md:hidden">
            <td colspan="2" style="padding: 0">
              <div class="border rounded-xl p-4 shadow-sm bg-white dark:bg-zinc-900 my-2 mx-1" style="border-color: #2d8a4e;">
                <div class="flex justify-between items-center">
                  <div>
                    <div class="text-xl font-bold">{{ position.name }}</div>
                    @if (position.position?.role === 'manager') {
                      <div class="text-xs text-surface-500 dark:text-surface-400 mt-1">
                        {{ 'This position has access to the management portal and the ability to add employees to the company' | transloco }}
                      </div>
                    }
                  </div>
                  <div>
                    <button 
                      pButton 
                      pRipple 
                      icon="pi pi-pencil" 
                      class="p-button-rounded p-button-text p-button-success"
                      (click)="editPosition(position)">
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  `,
  standalone: true,
  imports: [
    ButtonModule,
    TableModule,
    RippleModule,
    ToolbarModule,
    InputTextModule,
    HrPositionsForm,
    ConfirmDialogModule,
    ToastModule,
    TranslocoModule
  ],
  providers: [MessageService, ConfirmationService]
})
export class HrPositionsIndex implements OnInit {
  private companyService = inject(CompanyService);
  private messageService = inject(MessageService);
  private transloco = inject(TranslocoService);

  readonly ENABLE_ADD = false;
  readonly ENABLE_ARCHIVE = false;

  positions: ModelsCompanyPosition[] = [];
  positionDialog = false;
  position: ModelsCompanyPosition = {} as ModelsCompanyPosition;

  ngOnInit() {
    this.companyService.apiCompanyPositionsGet("true").subscribe((r: ApiCompanyPositionListDataResponse) => {
      if (r.data) {
        this.positions = r.data;
      }
    });
  }

  openNew() {
    this.position = {} as ModelsCompanyPosition;
    this.positionDialog = true;
  }

  hideDialog() {
    this.positionDialog = false;
  }

  savePosition(position: ModelsCompanyPosition) {
    const isNew = !position.id;
    if (isNew) {
      this.companyService.apiCompanyPositionsPost({ name: position.name!, position_id: position.template_position_id || 0 }).subscribe((r: ApiCompanyPositionDataResponse) => {
        if (r.data) {
          this.positions.push(r.data);
          this.positions = [...this.positions];
          this.positionDialog = false;
          this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Position Created') as string, life: 3000 });
        }
      });
    } else {
      this.companyService.apiCompanyPositionsIdPut(position.id!, { name: position.name! }).subscribe((r: ApiCompanyPositionDataResponse) => {
        if (r.data) {
          this.positions[this.findIndexById(position.id!)] = r.data;
          this.positions = [...this.positions];
          this.positionDialog = false;
          this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Position Updated') as string, life: 3000 });
        }
      });
    }
  }

  findIndexById(id: number): number {
    let index = -1;
    for (let i = 0; i < this.positions.length; i++) {
      if (this.positions[i].id === id) {
        index = i;
        break;
      }
    }
    return index;
  }

  editPosition(position: ModelsCompanyPosition) {
    this.position = { ...position };
    this.positionDialog = true;
  }
}
